/** Shared lifecycle UI helpers for the Live list + fixture dashboard. */
import React from 'react';

export const LIFECYCLE_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', hint: 'Pre-game: no in-play quoting.' },
  { value: 'live', label: 'Live', hint: 'In-play quoting per in_game market config.' },
  {
    value: 'suspended',
    label: 'Suspended',
    hint: 'Hold everything on this fixture (tier-1).',
    danger: true,
  },
  {
    value: 'complete',
    label: 'Complete',
    hint: 'Close out the fixture and drop its engines.',
    danger: true,
  },
];

/**
 * One button per game state instead of a dropdown + Apply.
 *
 * A dropdown hid which state was active behind a collapsed control and needed two clicks
 * to change it; during a live game that is the wrong trade. The button for the current
 * state is marked active and disabled, so the row always shows the state at a glance and
 * any change is a single click.
 */
export function LifecycleButtons({
  current,
  onSelect,
  onClearOverride,
  hasOverride,
  busy,
  size,
}) {
  const active = String(current || '').trim().toLowerCase();
  const cls = size === 'sm' ? ' live-lifecycle-buttons--sm' : '';
  return (
    <div className={`live-lifecycle-buttons${cls}`}>
      {LIFECYCLE_OPTIONS.map((o) => {
        const isActive = o.value === active;
        return (
          <button
            key={o.value}
            type="button"
            className={[
              'live-lifecycle-btn',
              isActive ? 'live-lifecycle-btn--active' : '',
              o.danger ? 'live-lifecycle-btn--danger' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={busy || isActive}
            title={isActive ? `Already ${o.label.toLowerCase()}` : o.hint}
            onClick={() => onSelect(o.value)}
          >
            {o.label}
          </button>
        );
      })}
      {hasOverride && onClearOverride && (
        <button
          type="button"
          className="live-lifecycle-btn live-lifecycle-btn--clear"
          disabled={busy}
          title="Clear manual override; resume automatic lifecycle"
          onClick={onClearOverride}
        >
          Clear lock
        </button>
      )}
    </div>
  );
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return err?.message || 'Request failed';
}

export function effectiveFixtureStatus(fixture) {
  if (!fixture) return '—';
  const ov = fixture.status_override;
  if (ov != null && String(ov).trim()) return String(ov).trim();
  if (fixture.effective_status != null && String(fixture.effective_status).trim()) {
    return String(fixture.effective_status).trim();
  }
  return fixture.status || '—';
}

/** Human-readable writer fan-out result from lifecycle / kill-switch API. */
export function describeWriterFanout(result) {
  if (!result) return null;
  const summary = result.writer_fanout_summary;
  const rows = result.writer_fanout;
  if (!summary && (!rows || rows.length === 0)) {
    return {
      level: 'muted',
      text: 'Sync writers: off (DB updated on api_only only).',
    };
  }
  if (!summary) return null;
  const n = summary.writers_called ?? 0;
  if (n === 0) {
    return {
      level: 'error',
      text:
        'Sync writers: 0 targets called — configure routing in api config ' +
        '(e.g. soccer: http://127.0.0.1:8001) and INTERNAL_AUTH_TOKEN. ' +
        (summary.warning || ''),
    };
  }
  const allOk = summary.all_writers_reachable;
  const writerBody = (rows || []).find((r) => r.writer_reachable && r.body)?.body;
  const persist = writerBody?.quote_persist;
  const persistLine =
    persist && typeof persist.markets_persisted === 'number'
      ? ` · ${persist.markets_persisted}/${persist.markets_total || '?'} markets persisted`
      : '';
  const failed = (rows || []).filter((r) => !r.writer_reachable);
  if (!allOk && failed.length) {
    const detail = failed
      .map((r) => `${r.routing_key || '?'}: ${r.error || r.url || 'failed'}`)
      .join('; ');
    return {
      level: 'error',
      text: `Sync writers: ${n} called, failures: ${detail}`,
    };
  }
  const keys = (rows || []).map((r) => r.routing_key || 'local').join(', ');
  return {
    level: 'ok',
    text: `Sync writers: ${n} OK (${keys})${persistLine}`,
  };
}

export function WriterFanoutBanner({ result }) {
  const info = describeWriterFanout(result);
  if (!info) return null;
  const cls =
    info.level === 'error'
      ? 'error-message live-fanout-banner'
      : info.level === 'ok'
        ? 'live-fanout-banner live-fanout-banner--ok'
        : 'live-muted live-fanout-banner';
  return <p className={cls}>{info.text}</p>;
}

export function statusBadge(fixtureOrStatus, override) {
  const eff =
    typeof fixtureOrStatus === 'object' && fixtureOrStatus !== null
      ? effectiveFixtureStatus(fixtureOrStatus)
      : override || fixtureOrStatus || '—';
  const cls =
    eff === 'live'
      ? 'live-status-badge live-status-badge--live'
      : eff === 'suspended'
        ? 'live-status-badge live-status-badge--sus'
        : eff === 'complete'
          ? 'live-status-badge live-status-badge--done'
          : 'live-status-badge';
  return <span className={cls}>{eff}</span>;
}
